/**
 * Auth API
 */

import apiClient from './client';
import { isAdminRole, UserRole } from '../utils/role';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
}

export interface LoginResponse {
  token: string;
  user: {
    id: string;
    email: string;
    name: string;
    role: string;
    tenantId?: string;
    customerId?: string;
  };
}

export interface User {
  id: string;
  email: string;
  name: string;
  role: string;
  tenantId?: string;
  customerId?: string;
  teamId?: string;
  note?: string;
  createdAt?: string;
  updatedAt?: string;
}

const TOKEN_KEY = 'agentbuilder_token';
const USER_KEY = 'agentbuilder_user';
// 마지막으로 로그인한 역할 - 로그아웃 후 로그인 화면도 그 역할 테마로 보여주기 위해 유지
const LAST_ROLE_KEY = 'agentbuilder_last_role';

export const authApi = {
  /**
   * 로그인
   */
  async login(data: LoginRequest): Promise<LoginResponse> {
    const response = await apiClient.post<{ success: boolean; data: LoginResponse }>(
      '/api/auth/login',
      data
    );
    const { token, user } = response.data.data;
    
    // 토큰 저장
    localStorage.setItem(TOKEN_KEY, token);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    
    // API 클라이언트에 토큰 설정
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;

    // 역할별 테마 적용 (새로고침 없이 계정 전환 시에도 반영)
    authApi.applyRoleTheme(user.role);

    return response.data.data;
  },

  /**
   * 회원가입
   */
  async register(data: RegisterRequest): Promise<void> {
    await apiClient.post('/api/auth/register', data);
  },

  /**
   * 로그아웃
   */
  logout(): void {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    delete apiClient.defaults.headers.common['Authorization'];
    authApi.applyRoleTheme();
  },

  /**
   * 역할별 테마 적용 - <html data-role> 설정 (themeAdmin.css / themeUser.css)
   * + 탭 아이콘(favicon)도 역할별 심볼로 (public/favicon-admin.svg · favicon-user.svg)
   */
  applyRoleTheme(role?: string): void {
    // role 없음(로그아웃·비로그인) → 마지막 로그인 역할, 그것도 없으면 사용자
    if (role) localStorage.setItem(LAST_ROLE_KEY, role);
    // 관리자 계열(제품/테넌트/고객사 관리자 + 레거시 admin)은 admin 테마
    const effectiveRole = role ?? localStorage.getItem(LAST_ROLE_KEY);
    const themeRole = isAdminRole(effectiveRole) ? 'admin' : 'user';
    document.documentElement.dataset.role = themeRole;
    const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    if (favicon) favicon.href = `/favicon-${themeRole}.svg`;
  },

  /**
   * 저장된 토큰 가져오기
   */
  getToken(): string | null {
    return localStorage.getItem(TOKEN_KEY);
  },

  /**
   * 저장된 사용자 정보 가져오기
   */
  getUser(): User | null {
    const userJson = localStorage.getItem(USER_KEY);
    if (!userJson) return null;
    try {
      return JSON.parse(userJson);
    } catch {
      return null;
    }
  },

  /**
   * 로그인 상태 확인
   */
  isAuthenticated(): boolean {
    return !!this.getToken();
  },

  /**
   * 앱 시작시 토큰 복원
   */
  restoreAuth(): void {
    const token = this.getToken();
    if (token) {
      apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  },

  /**
   * 현재 사용자 정보 조회 (서버에서)
   */
  async getCurrentUser(): Promise<User> {
    const response = await apiClient.get<{ success: boolean; data: User }>('/api/auth/me');
    return response.data.data;
  },

  /** 내 범위 내 사용자 목록 (멤버 선택 용도, 모든 역할 접근 가능) */
  async listPeers(): Promise<User[]> {
    const response = await apiClient.get<{ success: boolean; data: User[] }>('/api/auth/users/peers');
    return response.data.data;
  },

  /**
   * 사용자 목록 조회 (관리자 전용)
   */
  async listUsers(): Promise<User[]> {
    const response = await apiClient.get<{ success: boolean; data: User[] }>('/api/auth/users');
    return response.data.data;
  },

  /**
   * 사용자 역할 변경 (관리자 전용)
   */
  async updateUserRole(userId: string, role: UserRole): Promise<User> {
    const response = await apiClient.patch<{ success: boolean; data: User }>(
      `/api/auth/users/${userId}/role`,
      { role }
    );
    return response.data.data;
  },

  /**
   * 사용자 삭제 (관리자 전용)
   */
  async deleteUser(userId: string): Promise<void> {
    await apiClient.delete(`/api/auth/users/${userId}`);
  },

  /**
   * 사용자 정보 수정 (관리자 전용)
   */
  async updateUser(userId: string, data: { name?: string; role?: UserRole; tenantId?: string; customerId?: string; teamId?: string; note?: string }): Promise<User> {
    const response = await apiClient.patch<{ success: boolean; data: User }>(
      `/api/auth/users/${userId}`,
      data
    );
    return response.data.data;
  },

  /**
   * 사용자 초대 이메일 발송 (관리자 전용)
   */
  async inviteUsers(emails: string[], role: string, tenantId?: string, customerId?: string, teamId?: string): Promise<{ email: string; success: boolean }[]> {
    const response = await apiClient.post<{ success: boolean; data: { email: string; success: boolean }[] }>(
      '/api/auth/invite',
      { emails, role, tenantId, customerId, teamId }
    );
    return response.data.data;
  },

  /**
   * 초대 토큰 정보 조회 (인증 불필요)
   */
  async getInviteInfo(token: string): Promise<{ email: string; role: string; tenantId?: string; customerId?: string }> {
    const response = await apiClient.get<{ success: boolean; data: { email: string; role: string; tenantId?: string; customerId?: string } }>(
      `/api/auth/invite-info?token=${encodeURIComponent(token)}`
    );
    return response.data.data;
  },

  /**
   * 초대 토큰 기반 회원가입
   */
  async inviteRegister(token: string, name: string, password: string): Promise<void> {
    await apiClient.post('/api/auth/invite-register', { token, name, password });
  },

  /**
   * 현재 사용자를 관리자로 승격 (관리자가 없을 때만)
   */
  async makeAdmin(): Promise<User> {
    const response = await apiClient.post<{ success: boolean; data: User }>('/api/auth/make-admin');
    return response.data.data;
  },
};

export default authApi;
